import { api } from '@/api/client';
import type { OdooTask, EasykitDriverTask, EasykitSoSummary } from '@/types/odoo';

// Re-exports for backwards compatibility with existing call sites.
// New code should import these from '@/types/odoo' directly.
export type { EasykitCollector, EasykitSoSummary } from '@/types/odoo';

export interface EasykitOverlayTask {
  id: number;
  name: string;
  date_deadline: string;
  installerIds: number[];
  projectName: string | null;
  plName: string | null;
  soName: string | null;
  stageName: string | null;
  address: string | null;
  description: string | null;
}

export interface EasykitPlanningResponse {
  tasks: OdooTask[];
  driverMap: Record<number, string>;
  driverIds: number[];
  projectNameMap: Record<number, string>;
  addressMap: Record<number, string>;
  soSummaryMap: Record<number, EasykitSoSummary>;
  dayMarkers: Record<string, Record<number, string>>;
  overlayTasks: EasykitOverlayTask[];
  /** dateKey -> label of the main planning's company days (absent from an older API). */
  holidays?: Record<string, string>;
}

export async function fetchEasykitPlanning(from: string, to: string): Promise<EasykitPlanningResponse> {
  return api<EasykitPlanningResponse>(`/api/easykit/planning?from=${from}&to=${to}`);
}

export async function reorderEasykitTasks(taskIds: number[]): Promise<void> {
  await api('/api/easykit/planning/reorder', {
    method: 'POST',
    body: JSON.stringify({ taskIds }),
  });
}

export async function setEasykitDayStatus(
  date: string,
  driverId: number,
  newStatus: string,
): Promise<void> {
  await api('/api/easykit/planning/day-status', {
    method: 'POST',
    body: JSON.stringify({ date, driverId, newStatus }),
  });
}

export async function createEasykitTask(data: {
  type: string;
  date: string;
  driverId: number;
  name?: string;
  description?: string;
}): Promise<void> {
  await api('/api/easykit/tasks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEasykitTask(taskId: number, fields: { description?: string; name?: string; prefix?: string }): Promise<void> {
  await api(`/api/easykit/tasks/${taskId}`, {
    method: 'PATCH',
    body: JSON.stringify(fields),
  });
}

export async function moveEasykitTask(taskId: number, date: string, driverId: number): Promise<void> {
  await api(`/api/easykit/tasks/${taskId}/move`, {
    method: 'POST',
    body: JSON.stringify({ date, driverId }),
  });
}

export async function toggleEasykitTag(taskId: number, tagId: number, action: 'add' | 'remove'): Promise<void> {
  await api<void>(`/api/easykit/tasks/${taskId}/tags`, {
    method: 'POST',
    body: JSON.stringify({ tagId, action }),
  });
}

export async function setEasykitTaskOnHold(taskId: number, onHold: boolean): Promise<void> {
  await api<void>(`/api/easykit/tasks/${taskId}/hold`, {
    method: 'POST',
    body: JSON.stringify({ onHold }),
  });
}

/** EK installer record — used by the "Plan op iemand anders" modal (Slice 3). */
export interface EkInstaller {
  id: number;
  name: string;
  /** parent_id[1] when set; null when the partner has no company. */
  companyName: string | null;
  /** Whether this id is in BASE_EASYKIT_DRIVER_IDS (Dries / Guy / Olivier). */
  isBaseEasykitDriver: boolean;
}

export async function fetchEasykitInstallers(): Promise<EkInstaller[]> {
  return api<EkInstaller[]>('/api/easykit/installers');
}

export async function unscheduleEasykitTask(taskId: number): Promise<void> {
  await api<void>(`/api/easykit/tasks/${taskId}/unschedule`, { method: 'POST' });
}

export async function deleteEasykitTask(taskId: number): Promise<void> {
  await api<void>(`/api/easykit/tasks/${taskId}`, { method: 'DELETE' });
}

export interface EasykitSearchResult {
  id: number;
  name: string;
  projectName: string;
  orderNumber: string;
  address: string;
  dateDeadline: string | null;
  tagIds: number[];
  isPlanned: boolean;
  installerIds: number[];
}

export async function searchEasykitTasks(q: string): Promise<EasykitSearchResult[]> {
  const res = await api<{ results: EasykitSearchResult[] }>(`/api/easykit/tasks/search?q=${encodeURIComponent(q)}`);
  return res.results;
}

export async function fetchEasykitDriverTasks(): Promise<EasykitDriverTask[]> {
  const res = await api<{ tasks: EasykitDriverTask[] }>('/api/easykit/driver/tasks');
  return res.tasks;
}

export async function fetchEasykitDriverTasksById(driverId: number): Promise<EasykitDriverTask[]> {
  const res = await api<{ tasks: EasykitDriverTask[] }>(`/api/easykit/driver/${driverId}/tasks`);
  return res.tasks;
}

export interface TaskPhoto {
  key: string;
  url: string;
  filename: string;
  lastModified: string;
  size: number;
}

export async function uploadEasykitPhoto(taskId: number, file: File, projectName: string, taskName: string): Promise<{ url: string; key: string }> {
  const formData = new FormData();
  // Fields MUST come before the file — Fastify multipart reads fields only up to the file stream
  formData.append('projectName', projectName);
  formData.append('taskName', taskName);
  formData.append('file', file);
  return api<{ url: string; key: string }>(`/api/photos/task/${taskId}`, {
    method: 'POST',
    body: formData,
  });
}

export async function listEasykitPhotos(taskId: number, projectName?: string, taskName?: string): Promise<{ photos: TaskPhoto[]; total: number }> {
  const params = new URLSearchParams();
  if (projectName) params.set('projectName', projectName);
  if (taskName) params.set('taskName', taskName);
  const qs = params.toString();
  return api<{ photos: TaskPhoto[]; total: number }>(`/api/photos/task/${taskId}${qs ? `?${qs}` : ''}`);
}

export async function deleteEasykitPhoto(taskId: number, key: string): Promise<void> {
  await api<void>(`/api/photos/task/${taskId}?key=${encodeURIComponent(key)}`, { method: 'DELETE' });
}

export interface BrowserFolder {
  name: string;
  path: string;
}

export interface BrowserFile {
  key: string;
  url: string;
  filename: string;
  lastModified: string;
  size: number;
}

export interface RecentProject {
  name: string;
  path: string;
  photoCount: number;
  lastModified: string;
}

export async function fetchEasykitBrowse(prefix: string): Promise<{ folders: BrowserFolder[]; files: BrowserFile[] }> {
  return api<{ folders: BrowserFolder[]; files: BrowserFile[] }>(`/api/photos/easykit/browse?prefix=${encodeURIComponent(prefix)}`);
}

export async function deleteEasykitBrowserPhoto(key: string): Promise<void> {
  await api<void>(`/api/photos/easykit/browse?key=${encodeURIComponent(key)}`, { method: 'DELETE' });
}

export async function fetchEasykitRecentProjects(limit = 10): Promise<{ projects: RecentProject[] }> {
  return api<{ projects: RecentProject[] }>(`/api/photos/easykit/recent?limit=${limit}`);
}
